import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useEffect } from 'react';
import { MachineMapPoint } from '../../types/dashboard-types';

// Fix Leaflet default icon broken by webpack/vite asset handling
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Props {
  points: MachineMapPoint[];
  isLoading: boolean;
  error?: string | null;
  currency?: string | null;
}

const CURRENCY_CENTER: Record<string, [number, number]> = {
  SEK: [62.0, 15.0],   // Sweden
  NOK: [64.5, 17.0],   // Norway
  DKK: [56.0, 10.0],   // Denmark
  GBP: [54.0, -2.0],   // UK
  EUR: [50.0, 10.0],   // Europe
  USD: [39.5, -98.4],  // USA
  CAD: [56.1, -106.4], // Canada
  AUD: [-25.3, 133.8], // Australia
  NZD: [-41.3, 174.8], // New Zealand
  CHF: [46.8, 8.2],    // Switzerland
  ILS: [31.5, 34.9],   // Israel
  PLN: [52.2, 19.1],   // Poland
};

function markerColor(point: MachineMapPoint): string {
  if (!point.isConnected) return '#9ca3af';
  switch (point.status) {
    case 'Faulted': return '#ef4444';
    case 'Charging':
    case 'Preparing':
    case 'Finishing': return '#3b82f6';
    default: return '#22c55e';
  }
}

function createColorIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z"
        fill="${color}" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>
    </svg>`,
    iconSize: [24, 32],
    iconAnchor: [12, 32],
    popupAnchor: [0, -34],
  });
}

function BoundsController({ points }: { points: MachineMapPoint[] }) {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [map, points]);

  return null;
}

function DefaultCenterController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center[0], center[1], zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function formatLastSeen(date?: Date): string {
  if (!date) return 'Unknown';
  const diff = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function ChargerMapPanel({ points, isLoading, error, currency }: Props) {
  if (isLoading) {
    return (
      <div className="w-full h-[400px] rounded-xl border bg-muted animate-pulse flex items-center justify-center">
        <span className="text-muted-foreground text-sm">Loading map…</span>
      </div>
    );
  }

  const defaultCenter: [number, number] = (currency && CURRENCY_CENTER[currency.toUpperCase()])
    ?? [50.0, 10.0];
  const center: [number, number] = points.length > 0
    ? [points[0].latitude, points[0].longitude]
    : defaultCenter;

  if (error) {
    return (
      <div className="w-full h-[400px] rounded-xl border bg-muted flex items-center justify-center">
        <span className="text-destructive text-sm">Map error: {error}</span>
      </div>
    );
  }

  return (
    <div className="w-full h-[400px] rounded-xl overflow-hidden border relative">
      {points.length === 0 && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center pointer-events-none">
          <span className="bg-background/80 text-muted-foreground text-sm px-3 py-1.5 rounded-full shadow-sm">
            No location data available
          </span>
        </div>
      )}
      <MapContainer
        center={center}
        zoom={points.length > 0 ? 8 : 6}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <BoundsController points={points} />
        {points.length === 0 && <DefaultCenterController center={defaultCenter} zoom={5} />}
        {points.map(point => (
          <Marker
            key={point.machineId}
            position={[point.latitude, point.longitude]}
            icon={createColorIcon(markerColor(point))}
          >
            <Popup>
              <div className="text-sm min-w-[160px]">
                <div className="font-semibold mb-1">{point.machineName}</div>
                <div className="text-muted-foreground mb-1">{point.actorName}</div>
                <div className="flex items-center gap-1 mb-1">
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: markerColor(point) }}
                  />
                  <span>{point.isConnected ? point.status : 'Offline'}</span>
                </div>
                <div className="text-muted-foreground">
                  {point.connectorCount} connector{point.connectorCount !== 1 ? 's' : ''}
                </div>
                <div className="text-muted-foreground text-xs mt-1">
                  Last seen: {formatLastSeen(point.lastHeartbeat)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
